import { useRef, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { whatsappLink } from '@/mocks/gymInfo';
import { fetchCmsPlans, type CmsPlan } from '@/lib/cms';

// Fallback plans (used when the CMS is unreachable)
const fallbackPlans: CmsPlan[] = [
  { id: -1, name: { pt: 'Musculação Mensal', en: 'Monthly Weight Training' }, price: 'R$ 169', perMonth: true, priceNote: { pt: '', en: '' }, description: { pt: 'Acesso completo à área de musculação.', en: 'Full access to the weight training area.' }, highlighted: false },
  { id: -2, name: { pt: 'Musculação + 1 Modalidade', en: 'Weights + 1 Modality' }, price: 'R$ 180', perMonth: true, priceNote: { pt: '', en: '' }, description: { pt: 'Musculação e uma modalidade à sua escolha.', en: 'Weight training plus one modality of your choice.' }, highlighted: false },
  { id: -3, name: { pt: 'Plano Anual Completo', en: 'Full Annual Plan' }, price: '12x R$ 130', perMonth: false, priceNote: { pt: 'no cartão de crédito', en: 'on credit card' }, description: { pt: 'Todas as modalidades inclusas por 12 meses.', en: 'All modalities included for 12 months.' }, highlighted: true },
];

const fallbackPasses: CmsPlan[] = [
  { id: -4, name: { pt: 'Diária', en: 'Day pass' }, price: 'R$ 35', perMonth: false, priceNote: { pt: '', en: '' }, description: { pt: '', en: '' }, highlighted: false },
  { id: -5, name: { pt: 'Semana', en: 'Week' }, price: 'R$ 110', perMonth: false, priceNote: { pt: '', en: '' }, description: { pt: '', en: '' }, highlighted: false },
  { id: -6, name: { pt: 'Quinzena', en: 'Two weeks' }, price: 'R$ 130', perMonth: false, priceNote: { pt: '', en: '' }, description: { pt: '', en: '' }, highlighted: false },
];

export default function PricingSection() {
  const { t, i18n } = useTranslation();
  const sectionRef = useRef<HTMLElement>(null);
  const [plans, setPlans] = useState<CmsPlan[]>(fallbackPlans);
  const [passes, setPasses] = useState<CmsPlan[]>(fallbackPasses);

  const lang = (i18n.language?.startsWith('en') ? 'en' : 'pt') as 'pt' | 'en';
  const name = (p: CmsPlan) => p.name[lang] || p.name.pt;

  useEffect(() => {
    let cancelled = false;
    // A valid CMS response is authoritative, even when empty (admin may
    // deactivate plans on purpose). Fallbacks apply only if the CMS is unreachable.
    fetchCmsPlans().then((data) => {
      if (!cancelled && data) {
        setPlans(data.plans);
        setPasses(data.passes);
      }
    });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    const items = el.querySelectorAll('.reveal-item');
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const target = entry.target as HTMLElement;
            const index = Array.from(items).indexOf(target);
            target.style.transitionDelay = `${index * 100}ms`;
            target.classList.add('reveal-visible');
            observer.unobserve(target);
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -40px 0px' }
    );
    items.forEach((item) => observer.observe(item));
    return () => observer.disconnect();
  }, [plans, passes]);

  const planWhatsAppUrl = (planName: string) =>
    `${whatsappLink}?text=${encodeURIComponent(t('home.pricing.whatsappMessage') + planName)}`;

  // Nothing to show — admin disabled everything in the CMS
  if (plans.length === 0 && passes.length === 0) return null;

  return (
    <section ref={sectionRef} id="planos" className="bg-foreground-950 py-16 md:py-24">
      <div className="w-full px-4 md:px-6">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-10 md:mb-14">
          <h2 className="reveal-item reveal font-heading text-4xl md:text-6xl lg:text-7xl text-background-50 leading-tight">
            {t('home.pricing.title')}
          </h2>
          <p className="reveal-item reveal font-body text-base text-background-300 max-w-md">
            {t('home.pricing.subtitle')}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
          {plans.map((plan) => (
            <div
              key={plan.id}
              className={`reveal-item reveal relative rounded-lg p-6 md:p-8 flex flex-col transition-transform duration-500 hover:-translate-y-2 ${
                plan.highlighted
                  ? 'bg-primary-500 text-foreground-950'
                  : 'bg-foreground-900 text-background-50 border border-foreground-800'
              }`}
            >
              {plan.highlighted && (
                <div className="absolute -top-3 right-4 bg-background-50 text-foreground-950 font-body text-xs font-bold px-3 py-1 rounded-full">
                  {t('home.pricing.tagBestValue')}
                </div>
              )}
              <h3 className="font-heading text-2xl md:text-3xl">{name(plan)}</h3>
              <div className="mt-4 flex items-baseline gap-1">
                <span className="font-heading text-4xl md:text-5xl">{plan.price}</span>
                {plan.perMonth && (
                  <span className={`font-body text-sm ${plan.highlighted ? 'text-foreground-800' : 'text-background-400'}`}>
                    {t('home.pricing.perMonth')}
                  </span>
                )}
              </div>
              {(plan.priceNote[lang] || plan.priceNote.pt) && (
                <p className={`font-body text-xs mt-1 ${plan.highlighted ? 'text-foreground-800' : 'text-background-400'}`}>
                  {plan.priceNote[lang] || plan.priceNote.pt}
                </p>
              )}
              <p className={`font-body text-sm mt-4 flex-1 ${plan.highlighted ? 'text-foreground-800' : 'text-background-300'}`}>
                {plan.description[lang] || plan.description.pt}
              </p>
              <a
                href={planWhatsAppUrl(name(plan))}
                target="_blank"
                rel="noopener noreferrer"
                className={`btn-hover mt-6 inline-flex items-center justify-center gap-2 font-body text-sm font-semibold px-6 py-3 rounded-full whitespace-nowrap ${
                  plan.highlighted
                    ? 'bg-foreground-950 hover:bg-foreground-900 text-background-50'
                    : 'bg-primary-500 hover:bg-primary-600 text-foreground-950'
                }`}
              >
                <i className="ri-whatsapp-line text-base"></i>
                {t('home.pricing.cta')}
              </a>
            </div>
          ))}
        </div>

        {passes.length > 0 && (
          <div className="reveal-item reveal mt-8 md:mt-10 bg-foreground-900 border border-foreground-800 rounded-lg p-6 md:p-8">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
              <div>
                <h3 className="font-heading text-2xl md:text-3xl text-background-50">
                  {t('home.pricing.passes.title')}
                </h3>
                <p className="font-body text-sm text-background-300 mt-1">
                  {t('home.pricing.passes.subtitle')}
                </p>
              </div>
              <div className="flex flex-wrap gap-3 md:gap-4">
                {passes.map((pass) => (
                  <a
                    key={pass.id}
                    href={planWhatsAppUrl(`${name(pass)} (${pass.price})`)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn-hover flex flex-col items-center bg-foreground-950 hover:bg-foreground-800 border border-foreground-800 rounded-lg px-5 py-3 transition-colors"
                  >
                    <span className="font-body text-xs text-background-400">{name(pass)}</span>
                    <span className="font-heading text-xl text-primary-500">{pass.price}</span>
                  </a>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
